/**
 * DOM-free helpers behind `components/play-flow/DispatchInspector.tsx` (PR D,
 * U6b; R9 / Key Technical Decision 11). Everything here is a pure function
 * over the wire shapes `schemas.ts` already defines (U6a) — no fetch, no
 * React, no DOM — so it runs under `bun:test` and is exercised directly by
 * `__tests__/dispatch-inspector-logic.test.ts` without mounting anything.
 *
 * Merge-field send-prd U7 additions (R11/R12/R16, Key Technical Decisions 7
 * and 8): the pure helpers behind the "Test sends" list. Still DOM-free.
 *
 * Plan 2026-09-27-003 (U6): the per-row status words, the Clay status line,
 * the "Clay rendered" panel and the raw "Clay response" JSON are GONE — every
 * rep-facing status now comes from the one classifier,
 * `./dispatch-rep-status.ts`, which builds on the transport/START helpers
 * kept here.
 */
import {
  COPY_DEFECT_ERROR_PREFIX,
  EMPTY_FALLBACK_BY_LABEL,
  SENDER_MISSING_ERROR_PREFIX,
} from '../_shared/launcher-copy-tokens';
import { BUILD_FAILED_ERROR_PREFIX, MEMBER_REMOVED_ERROR_PREFIX } from './send-skip-prefixes';
import { RECHECK_STALE_AFTER_MS, rowStatusAnchor } from './dispatch-recheck-eligibility';
// Both from `step-content-rules.ts`, the DOM-free leaf — NOT from
// `gong-flow-picker-api.ts` (which re-exports `gongStepTypeLabel` for its
// older importers). That file imports `./mutations` → `./api-client`, and
// reaching the label through it dragged the fetch layer into every test that
// mounts `DispatchInspector`, breaking
// `__tests__/dispatch-inspector-clay-render.test.tsx`'s `mock.module`
// (`SyntaxError: Export named 'archiveIdentityRequest' not found`). Keep this
// import pointed at the leaf.
import { gongStepTypeLabel, isGongEmailStepType } from './step-content-rules';
import { playsPrefixKey } from './query-keys';
import type { PlaybooksPlayLogPageQuery } from './schemas';
import {
  CLAY_FINAL_STATUS_NON_SEND,
  readClayRunResultData,
  readDispatchFailure,
  readDispatchReconcile,
  ClayRunResultStatusSchema,
  type ClayRunResultStatus,
} from '../_shared/clay-run-status-schema';
import { z } from 'zod';

import {
  DispatchAutoRetryHistorySchema,
  DispatchCrashTriageSchema,
  DispatchPriorRunsSchema,
  type DispatchAutoRetryEntry,
  type DispatchCrashTriage,
  type DispatchPriorRun,
} from './schemas';
import type {
  PlaybooksDispatchLogRow,
  PlaybooksRetryDispatchOutcome,
  PlaybooksRetryDispatchResponse,
  PlaybooksValueDefect,
} from './schemas';

// Status bucketing
//
// `launcher_dispatch_log` has no single "status" column — `result` carries
// whatever terminal-ish label the worker writes (`null`/`'queued'` until it
// does), and `processing_at` is the ONE column that distinguishes "the
// worker has claimed this row" from "still sitting in the queue" before a
// terminal `result` lands. The mapping, in priority order:
//
//   1. `result === 'sent'`            → sent      (terminal, wins over processing_at)
//   2. `result === 'failed'`          → failed    (terminal, wins over processing_at)
//   3. `result === 'processing'`      → processing
//      OR `processing_at !== null` (and result isn't a terminal value above)
//   4. `result === null || 'queued'`  → queued     (not yet claimed)
//   5. anything else non-null         → other      (future/unrecognized
//      vocabulary kept VISIBLE rather than silently folded into a known
//      bucket — an inspector that hides an unrecognized result is lying
//      about what happened)

export type DispatchResultCategory = 'queued' | 'processing' | 'sent' | 'failed' | 'other';

/** One row's bucket — see the header comment above for the exact mapping. */
export function classifyDispatchResult(
  row: Pick<PlaybooksDispatchLogRow, 'result' | 'processing_at'>,
): DispatchResultCategory {
  if (row.result === 'sent') return 'sent';
  if (row.result === 'failed') return 'failed';
  if (row.result === 'processing' || row.processing_at !== null) return 'processing';
  if (row.result === null || row.result === 'queued') return 'queued';
  return 'other';
}

export interface DispatchResultCounts {
  queued: number;
  processing: number;
  sent: number;
  failed: number;
  other: number;
}

/** Per-send summary badges: how many rows land in each bucket. */
export function countByResult(
  rows: readonly Pick<PlaybooksDispatchLogRow, 'result' | 'processing_at'>[],
): DispatchResultCounts {
  const counts: DispatchResultCounts = { queued: 0, processing: 0, sent: 0, failed: 0, other: 0 };
  for (const row of rows) {
    counts[classifyDispatchResult(row)] += 1;
  }
  return counts;
}

/**
 * How long a `sent` row with no Clay status (or a `clay_pending` one) is still
 * worth waiting on: `launcher.clay-run-status`'s own give-up budget
 * (`CLAY_POLL_BUDGET_MS`, ~20 min) plus one minute, so the inspector keeps
 * refreshing until just after the poller's final `clay_not_found` write lands
 * rather than going quiet a moment before it. The same number is the
 * re-check's stale window (`dispatch-recheck-eligibility.ts`) — one figure,
 * so the inspector never asks for a re-check the server refuses.
 *
 * Since plan 2026-10-14-002 U0d the window runs from when the row actually
 * STARTED (`rowStatusAnchor`), so a paced send's last member is watched for
 * the same ~21 minutes as its first. A callback-armed routine row has no
 * per-row poll any more; the send's `playbooks.dispatch-settle-sweep` reads it
 * from 5 minutes on and keeps going past this window (up to 24 h) with the
 * same settler the re-check uses — so past this point the inspector's own
 * re-check and the sweep can only agree.
 */
export const CLAY_STATUS_WAIT_MS = RECHECK_STALE_AFTER_MS;

/**
 * How long a `clay_running` row is still worth waiting on, by Clay's last
 * word. Longer than {@link CLAY_STATUS_WAIT_MS} because that figure is the
 * status poller's give-up time, not V3's ceiling. Measured since 2026-09-01:
 * max 12.8 min, all long runs on Inflection. A reconciled `clay_running` row
 * has no child poll behind it, so this is its only stop; 30 minutes is kept
 * (plan 2026-09-27-003, U1) — the cost of waiting is one 5s refetch while the
 * inspector is open.
 */
export const CLAY_RUNNING_WAIT_MS = 30 * 60_000;

/**
 * How long a network-error crash Terra's own triage has CLAIMED
 * (`crash_triage.outcome = 'checking'`) keeps the Dispatch log refreshing: the
 * longest backoff (15 min) plus the Gong ask and slack (second review-fix pass
 * U3, C2-1).
 */
export const CRASH_TRIAGE_LIVE_WINDOW_MS = 20 * 60_000;

/**
 * How long a network-error crash NO triage has claimed yet keeps it refreshing:
 * the callback starts the triage at once, and the settle sweep hands an
 * unclaimed crash off within one 2-minute look (U3).
 */
export const TRANSPORT_CRASH_HANDOFF_WINDOW_MS = 5 * 60_000;

/** Columns `shouldPoll` reads — the transport state AND the Clay run state. */
type PollableRow = Pick<
  PlaybooksDispatchLogRow,
  'result' | 'processing_at' | 'result_status' | 'dispatched_at' | 'result_at' | 'result_data'
>;

/**
 * True only while SOMETHING could still change a row.
 *
 * Three independent reasons, in the order they occur over a row's life:
 *
 *   1. the dispatch itself is `queued`/`processing` — the original rule.
 *      `'other'` is deliberately NOT polling-worthy: an unrecognized result
 *      is not a state this UI knows how to watch resolve, and polling on it
 *      would never stop.
 *   2. Clay's run is `clay_pending` AND Clay said so within the last
 *      {@link CLAY_STATUS_WAIT_MS}, or `clay_running` within the last
 *      {@link CLAY_RUNNING_WAIT_MS} (by `rowStatusAnchor`: `result_at`, else
 *      `processing_at` — when the row actually started — else `dispatched_at`;
 *      a V3 run legitimately runs far longer than a pending one waits). The other `clay_*` values are terminal. The age
 *      bound matters for a RECONCILED run (plan 2026-09-26-003): it is
 *      `clay_running` with no child poll behind it, so only Clay's own
 *      callback can move it — if that never arrives, the inspector must still
 *      stop refreshing.
 *   3. the row is `sent` and Clay has said NOTHING yet, and it STARTED
 *      (`processing_at`) less than {@link CLAY_STATUS_WAIT_MS} ago. The age bound is what keeps
 *      the history from polling forever: every send from before this feature
 *      existed is `sent` with a null `result_status` and will never gain one.
 *      A `failed` row is excluded outright — Clay never received it.
 *   4. Terra is still deciding about a NETWORK-error crash (`isTransportCrash`;
 *      second review-fix pass U3, C2-1): its triage claimed THIS run
 *      (`crash_triage.run` = the row's `routine_run_id`, `outcome = 'checking'`)
 *      within {@link CRASH_TRIAGE_LIVE_WINDOW_MS} — it is sleeping its 1/5/15-min
 *      backoff before an automatic retry or a Gong repair — or no triage has
 *      claimed this run yet and the row settled within
 *      {@link TRANSPORT_CRASH_HANDOFF_WINDOW_MS}. Every terminal note
 *      (`manual_only`, `gong_unavailable`, `cap_reached`, `no_payload`,
 *      `repaired_enrolled`) ends it at once; `requeued` makes the row `queued`,
 *      which reason 1 covers. Nothing here retries anything.
 */
export function shouldPoll(rows: readonly PollableRow[], nowMs: number = Date.now()): boolean {
  const counts = countByResult(rows);
  if (counts.queued > 0 || counts.processing > 0) return true;
  return rows.some((row) => clayRunCouldStillChange(row, nowMs) || crashRecoveryInFlight(row, nowMs));
}

/** Reason 4 of {@link shouldPoll}. */
function crashRecoveryInFlight(row: PollableRow, nowMs: number): boolean {
  if (!isTransportCrash(row)) return false;
  const runId = readClayRunResultData(row.result_data)?.clay_run.routine_run_id ?? null;
  const triage = readCrashTriage(row.result_data);
  if (triage !== null && triage.run === runId) {
    return triage.outcome === 'checking' && isRecent(triage.at, nowMs, CRASH_TRIAGE_LIVE_WINDOW_MS);
  }
  return isRecent(rowStatusAnchor(row), nowMs, TRANSPORT_CRASH_HANDOFF_WINDOW_MS);
}

function clayRunCouldStillChange(row: PollableRow, nowMs: number): boolean {
  const status = parseClayRunStatus(row.result_status);
  if (status !== null) {
    if (status !== 'clay_pending' && status !== 'clay_running') return false;
    const windowMs = status === 'clay_running' ? CLAY_RUNNING_WAIT_MS : CLAY_STATUS_WAIT_MS;
    return isRecent(rowStatusAnchor(row), nowMs, windowMs);
  }
  // No Clay status at all: only worth waiting on for a recently-STARTED row.
  if (row.result !== 'sent') return false;
  return isRecent(rowStatusAnchor(row), nowMs);
}

/**
 * Within `windowMs` of `nowMs` — {@link CLAY_STATUS_WAIT_MS} by default,
 * {@link CLAY_RUNNING_WAIT_MS} for a `clay_running` row. An absent or
 * unparseable timestamp is treated as too old — an unreadable date must not
 * license an unbounded poll.
 */
function isRecent(timestamp: string | null, nowMs: number, windowMs: number = CLAY_STATUS_WAIT_MS): boolean {
  const at = timestamp === null ? Number.NaN : Date.parse(timestamp);
  if (Number.isNaN(at)) return false;
  return nowMs - at < windowMs;
}

/**
 * `result_status` narrowed to the `clay_*` vocabulary, or `null`.
 *
 * `null` for anything else on purpose: the column predates this feature and
 * older `/dispatch-result` callbacks wrote their own words there. Reading one
 * of those as a Clay run status would mislabel the row.
 */
function parseClayRunStatus(value: string | null): ClayRunResultStatus | null {
  const parsed = ClayRunResultStatusSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * Deterministic display order: by `contact_name` ascending (a `null` name
 * sorts first, as `''`), then by `id` ascending to break a tie — including
 * the all-null-names case, so the list order never depends on however the
 * server or the DB happened to return rows.
 *
 * Returns a NEW array; never mutates `rows` (the query-cache array it is
 * usually called on must stay untouched).
 */
export function sortLogRows<T extends { contact_name: string | null; id: string }>(
  rows: readonly T[],
): T[] {
  return [...rows].sort((a, b) => {
    const nameA = a.contact_name ?? '';
    const nameB = b.contact_name ?? '';
    if (nameA !== nameB) return nameA < nameB ? -1 : 1;
    if (a.id !== b.id) return a.id < b.id ? -1 : 1;
    return 0;
  });
}

/**
 * The `GET /plays/:id/sends` query key — shared by `DispatchInspector`'s own
 * fetch and `ReviewStep.handleSend`'s post-send `invalidateQueries` call (the
 * U6b marker), so the two can never drift onto different keys.
 */
export function playSendsQueryKey(playId: string): readonly [...ReturnType<typeof playsPrefixKey>, string, 'sends'] {
  return [...playsPrefixKey(), playId, 'sends'];
}

// Payload rendering helpers

export interface OverrideStepView {
  subject: string;
  body: string;
  /**
   * The builder's convention is `{subject, body, action_type}`
   * (`send-payload.ts#OverrideStep`) — this is that field, read off the raw
   * payload as `action_type` and renamed here for camelCase consistency with
   * the rest of this module. `''` when absent or non-string (a payload built
   * before U1/U4, or a malformed entry), same conservative-default posture as
   * `subject`/`body` below. Used by {@link gongRunsStepCaption} to tell a
   * non-email slot Terra sent out BLANK apart from a truly-missing email/reply
   * copy slot — a bare `subject === '' && body === ''` check can't make that
   * distinction on its own (plan
   * `2026-09-24-004-playbooks-gong-nonemail-steps-editable-plan.md`, U1d).
   */
  actionType: string;
}

/**
 * Pulls `overrideSteps` out of a dispatch-payload response body, which is
 * `z.record(z.string(), z.unknown())` on the wire (`schemas.ts`) — the UI has
 * no schema guarantee on its shape, only the send-payload builder's OWN
 * convention (`send-payload.ts#toOverrideSteps`, U3): an array of
 * `{subject, body, action_type}`. This never throws on a malformed entry — a
 * bad subject/body/action_type coerces to `''` rather than crashing the
 * inspector, and a missing/non-array `overrideSteps` returns `[]`.
 */
export function extractOverrideSteps(payload: unknown): OverrideStepView[] {
  if (payload === null || typeof payload !== 'object') return [];
  const steps = (payload as Record<string, unknown>).overrideSteps;
  if (!Array.isArray(steps)) return [];

  return steps.map((entry): OverrideStepView => {
    if (entry === null || typeof entry !== 'object') return { subject: '', body: '', actionType: '' };
    const record = entry as Record<string, unknown>;
    return {
      subject: typeof record.subject === 'string' ? record.subject : '',
      body: typeof record.body === 'string' ? record.body : '',
      actionType: typeof record.action_type === 'string' ? record.action_type : '',
    };
  });
}

/**
 * The tertiary caption shown after a preview step's subject line when that
 * step's stored subject was dropped (`PlaybooksMemberResolutionSchema`'s
 * `replySubjectsOmitted`, playbooks-widen-copy-defect-detection) — Gong
 * treats that step as a reply on the bound flow and threads it under step
 * 1's subject, so whatever the rep authored there never actually goes out as
 * its own subject line. `stepNumber` is 1-based, matching `replySubjectsOmitted`'s
 * own wire convention (`PlaybooksMemberResolutionSchema`'s doc). `null` when
 * this step isn't in the list, so the caller renders nothing rather than an
 * empty line.
 */
/**
 * `resolution.steps` (`PlaybooksMemberResolutionSchema`) keyed by the ORDINAL
 * index each entry occupies in `overrideSteps` / `extractOverrideSteps`'s
 * output — the SAME numbering the payload preview renders by (0-based array
 * index; `replySubjectOmittedCaption`'s 1-based `stepNumber` is that index
 * plus one), NOT the entry's own `position` field.
 *
 * `resolution.steps` covers AUTHORED steps only, one entry per step, pushed
 * by `send-payload.ts#toOverrideSteps` in the SAME loop — and therefore the
 * SAME order — as `overrideSteps` itself: both walk
 * `[...steps].sort((a, b) => a.position - b.position)` once. That makes each
 * entry's ORDINAL simply its own index in `resolution.steps` — never its
 * `position` field.
 *
 * `position` is the step's AUTHORED, possibly non-contiguous slot: a rep
 * deleting or reordering a step leaves gaps (e.g. authored positions
 * `[0, 2]`), and `position` only equals the array index when every position
 * happens to already be contiguous from 0. Keying by `position` instead of
 * the array index attaches a step's resolution to the WRONG rendered row
 * whenever a gap exists — with authored positions `[0, 2]`, position-keying
 * maps ordinal 2 (a blank padding step nobody wrote) to the resolution meant
 * for ordinal 1 (the second real step), and leaves ordinal 1 with no
 * resolution at all. This is the fix for exactly that mislabelling.
 */
export function stepResolutionsByOrdinal<T>(steps: readonly T[]): Map<number, T> {
  return new Map(steps.map((step, ordinal) => [ordinal, step]));
}

/**
 * The override steps a rep should see: the payload's `overrideSteps` is a
 * FIXED 20-slot array blank-padded after the authored steps, and only authored
 * steps carry a `resolution.steps` entry (`send-payload.ts` pushes one per
 * authored step, including a blank Gong-run Call/LinkedIn step). So the first
 * `resolution.steps.length` slots are the rep's steps and the rest are pads.
 * With no resolutions there is nothing to measure against: pass through.
 */
export function visibleOverrideSteps<T>(steps: readonly T[], resolution: { steps: readonly unknown[] }): T[] {
  return resolution.steps.length > 0 ? steps.slice(0, resolution.steps.length) : [...steps];
}

export function replySubjectOmittedCaption(
  stepNumber: number,
  replySubjectsOmitted: readonly number[],
): string | null {
  if (!replySubjectsOmitted.includes(stepNumber)) return null;
  return "Subject omitted — Gong sends this step as a reply and threads it under step 1's subject.";
}

/**
 * The tertiary caption for a non-email slot Terra sent out BLANK because the
 * rep left that step blank in Terra (`send-payload.ts#toOverrideSteps`'s
 * `gongRunsStep` fold, plan
 * `2026-09-24-004-playbooks-gong-nonemail-steps-editable-plan.md`, U1d) — no
 * override reached Gong, so Gong runs its own script for that step.
 *
 * `null` for every other row so the caller falls back to its existing
 * "(no subject)"/"(no body)" placeholders: an EMAIL slot that happens to be
 * blank is a real missing-copy defect, and a non-email slot that carries text
 * (`body !== ''`) is a REAL override the rep wrote — it renders its resolved
 * body like any other row, with no caption.
 *
 * Decided on the entry's OWN `actionType` (the wire `action_type`, mirrored
 * from `gong_flows` at send time — `send-payload.ts#toTargetFlowShape`), not
 * on the step's authored descriptor: the inspector only ever sees the
 * resolved payload, never the authored `SequenceStep`.
 */
export function gongRunsStepCaption(step: OverrideStepView): string | null {
  if (step.subject !== '' || step.body !== '') return null;
  if (isGongEmailStepType(step.actionType)) return null;
  const label = gongStepTypeLabel({ step_type: step.actionType }) ?? step.actionType;
  return `Gong runs this step — ${label}`;
}

// Skipped-member rows (U7 / Key Technical Decision 5; QA round 8, U6) — a
// `launcher_dispatch_log` row for a member who was NOT dispatched is tagged by
// a STABLE prefix. There are now two: `COPY_DEFECT_ERROR_PREFIX` (written by
// `_shared`'s `formatCopyDefectError` — the member's copy carried an unknown
// token) and `SENDER_MISSING_ERROR_PREFIX` (written by `send.ts` — the rung's
// met-with sender could not address this member). These helpers are the ONLY
// place the inspector parses either prefix back out, so a wording change to
// one only ever touches its owning module plus this file.

/** True when `error` is a copy-defect row rather than a transport/workflow failure. */
export function isCopyDefectError(error: string | null | undefined): boolean {
  return typeof error === 'string' && error.startsWith(COPY_DEFECT_ERROR_PREFIX);
}

/**
 * True when `error` is a SENDER skip (QA round 8, U6) — the member's copy was
 * fine; there was nobody to send as.
 */
export function isSenderMissingError(error: string | null | undefined): boolean {
  return typeof error === 'string' && error.startsWith(SENDER_MISSING_ERROR_PREFIX);
}

/**
 * True when `error` is a `member_removed:` row (plan 2026-10-14-002 U0b): the
 * person was in the approved, frozen roster but had left the campaign by the
 * time the background build reached them.
 */
export function isMemberRemovedError(error: string | null | undefined): boolean {
  return typeof error === 'string' && error.startsWith(MEMBER_REMOVED_ERROR_PREFIX);
}

/**
 * True when `error` is a `build_failed:` row (plan 2026-10-14-002 U0b): Terra
 * could not build this person's message, so there is no stored job — and so
 * nothing a START retry could re-send.
 */
export function isBuildFailedError(error: string | null | undefined): boolean {
  return typeof error === 'string' && error.startsWith(BUILD_FAILED_ERROR_PREFIX);
}

/**
 * True when `error` is ANY Terra-side hold — this member was deliberately held
 * back (or could not be prepared), not a transport or workflow failure. The
 * distinction matters to the rep: a hold is never a "Failed to start" with a
 * Retry, because nothing ever tried to start.
 */
export function isSkippedMemberError(error: string | null | undefined): boolean {
  return (
    isCopyDefectError(error)
    || isSenderMissingError(error)
    || isMemberRemovedError(error)
    || isBuildFailedError(error)
  );
}

/**
 * The rep-facing half of a skipped-member `error` string — whichever prefix it
 * carries, stripped and trimmed. Called on a transport error it returns the
 * string unchanged (never throws).
 */
export function skippedMemberSummary(error: string): string {
  if (isCopyDefectError(error)) return error.slice(COPY_DEFECT_ERROR_PREFIX.length).trim();
  if (isSenderMissingError(error)) return error.slice(SENDER_MISSING_ERROR_PREFIX.length).trim();
  if (isMemberRemovedError(error)) return error.slice(MEMBER_REMOVED_ERROR_PREFIX.length).trim();
  if (isBuildFailedError(error)) return error.slice(BUILD_FAILED_ERROR_PREFIX.length).trim();
  return error;
}

// Dispatch START failures — a launcher-workflow-level row error, distinct from
// the skipped-member prefixes above (those are pre-dispatch decisions THIS
// app made; these are the launcher's own worker reporting a transport
// failure). `workflows.ts` writes two shapes verbatim into `error`:
//
//   `[launcher.dispatch] Clay routine run could not be started for member
//   <id>: <reason>: HTTP <status>: <clay response body>`
//   `[launcher.dispatch] Webhook returned HTTP <status> for member <id>`
//
// Both are true but unreadable to a rep — a bracketed internal tag, a bare
// HTTP status, sometimes a raw JSON blob. `dispatchStartFailureSummary` maps
// EITHER known shape to one short plain-English line (CLAUDE.md: "never show
// a rep a raw status code"); an unrecognized string returns `null` so the
// caller falls through to showing it unchanged rather than a wrong summary.

const DISPATCH_START_FAILURE_PATTERNS: readonly RegExp[] = [
  /^\[launcher\.dispatch\] Clay routine run could not be started for member /,
  /^\[launcher\.dispatch\] Webhook returned HTTP \d+ for member /,
];

/** The plain-English line every known dispatch-start failure shares. */
export const DISPATCH_START_FAILURE_BASE = 'Clay turned this send away before it started.';

/**
 * Maps a known `launcher.dispatch` start-failure `error` string to one short
 * plain-English line, with Clay's own message text appended when the string
 * carries a trailing JSON body with a `message` field (the routine-run 400
 * case) — never "HTTP 400", never the `[launcher.dispatch]` tag. Returns
 * `null` for any string that doesn't match a known shape, so the caller shows
 * the raw string unchanged rather than a confidently wrong summary.
 *
 * The raw string is NEVER discarded from storage — it stays in `row.error` —
 * but it is no longer shown to every rep. `WhatClayDidPanel.tsx` renders it
 * behind a "Details (admin)" disclosure gated to launcher admins and an
 * active impersonation session (PR1, `plans/2026-09-30-001-…`); a plain rep
 * never sees it. This function only produces the plain-English headline.
 */
export function dispatchStartFailureSummary(error: string): string | null {
  if (!DISPATCH_START_FAILURE_PATTERNS.some((pattern) => pattern.test(error))) return null;

  const jsonStart = error.indexOf('{');
  if (jsonStart === -1) return DISPATCH_START_FAILURE_BASE;

  try {
    const parsed: unknown = JSON.parse(error.slice(jsonStart));
    const message =
      parsed !== null && typeof parsed === 'object' && 'message' in parsed
        ? parsed.message
        : undefined;
    if (typeof message === 'string' && message.trim() !== '') {
      return `${DISPATCH_START_FAILURE_BASE} ${message.trim()}`;
    }
  } catch {
    // Not parseable JSON (a plain-text Clay response, a truncated excerpt) —
    // fall through to the base sentence with no extra detail.
  }
  return DISPATCH_START_FAILURE_BASE;
}

/**
 * U0f — one placeholder-shaped Salesforce value, as the sentence a rep reads
 * on the preview step and under "What was sent" (`'now'`, see
 * {@link DataWarningTense}). What is used instead is READ from the
 * vocabulary's own declared fallback (never restated
 * here), so changing a token's fallback changes this sentence with no edit.
 * A member with such a value still sends — this is a warning about the
 * record, never about the rep's copy.
 */
export function dataWarningSentence(defect: PlaybooksValueDefect, tense: DataWarningTense): string {
  const fallback = EMPTY_FALLBACK_BY_LABEL.get(defect.token);
  const now = tense === 'now';
  const head = `{{${defect.token}}} is a placeholder (${defect.value}) in Salesforce${now ? ' right now' : ''}`;
  if (fallback === undefined) return `${head}.`;
  if (fallback.kind === 'drop-line') return now ? `${head}, so Terra leaves that line out.` : `${head}, so that line will be left out.`;
  if (fallback.text === '') return now ? `${head}, so Terra leaves it out.` : `${head}, so it will be left out.`;
  return now ? `${head}, so Terra uses "${fallback.text}" instead.` : `${head}, so "${fallback.text}" will be used instead.`;
}

/**
 * Which surface speaks (review-fix U12, C5-4): `'will'` — the resolution
 * report, about the send that is about to happen; `'now'` — "What was sent",
 * which reads the person's record as it is NOW, so it must not claim what
 * went out at send time.
 */
export type DataWarningTense = 'will' | 'now';

/** The caption over "What was sent"'s data warnings (rulebook (p)). */
export const SENT_COPY_DATA_WARNING_CAPTION = "From this person's Salesforce record as it is now — not a recording of the message.";

/** The short form of {@link dataWarningSentence}, for a badge. */
export function dataWarningBadgeText(defect: Pick<PlaybooksValueDefect, 'token'>): string {
  return `{{${defect.token}}} is a placeholder in Salesforce`;
}

// Polling across BOTH real sends and test dispatches (U7). Since U0a the rows
// are not on the wire, so this reads the server's `summary.live` — computed by
// `send-summary.ts#summarizeSendRows` from `shouldPoll` (above, with its
// age windows) plus "the background build is still running". `shouldPoll`
// itself stays a single-list primitive: the server uses it per send.

export function anySendLive(
  sends: readonly { summary: { live: boolean } }[],
  testSends: readonly { summary: { live: boolean } }[],
): boolean {
  return sends.some((send) => send.summary.live) || testSends.some((send) => send.summary.live);
}

/**
 * One send's paged-rows query key. It lives UNDER {@link playSendsQueryKey} on
 * purpose: Retry, the Clay re-check and the Review step's post-send
 * invalidation already invalidate the sends key, and that prefix match is what
 * refreshes an open card's rows with them.
 */
export function sendLogQueryKey(
  playId: string,
  dispatchId: string,
  status: string,
): readonly [...ReturnType<typeof playSendsQueryKey>, string, 'log', string] {
  return [...playSendsQueryKey(playId), dispatchId, 'log', status];
}

/**
 * The play log's query key (plan 2026-10-15-004 B1 — the Dispatch log's "All
 * people" view). UNDER {@link playSendsQueryKey} for the same reason as
 * {@link sendLogQueryKey}: post-send, Retry and the Clay re-check already
 * invalidate the sends key, and that prefix match refreshes this table too.
 * `'log'` sits where `sendLogQueryKey` has a dispatch id — no dispatch id is
 * the literal `log`, so the two never collide. No person = `''`.
 */
export function playLogQueryKey(
  playId: string,
  filters: Pick<PlaybooksPlayLogPageQuery, 'kind' | 'status' | 'memberId'>,
): readonly [...ReturnType<typeof playSendsQueryKey>, 'log', string, string, string] {
  return [...playSendsQueryKey(playId), 'log', filters.kind, filters.status, filters.memberId ?? ''];
}

/**
 * The dispatch id of the newest test send in a sends response (the server
 * sorts `testSends` newest first), or `null`. The Review step opens this card
 * after a refused "test already running" click, so the rep lands on the run
 * that blocked it.
 */
export function newestTestDispatchId(
  data: { testSends: readonly { dispatchId: string }[] } | undefined,
): string | null {
  return data?.testSends[0]?.dispatchId ?? null;
}

// Clay's own refusal vocabulary — ONE table, two readers
//
// The rep-facing words live here rather than in `dispatch-rep-status.ts`
// because two modules need them and neither may import the other:
// `dispatch-rep-status.ts` (the ONE classifier, which turns a reason into the
// sentence a rep reads) already imports this file, and the retry's eligibility
// test below needs the same table to decide whether a refusal is one Clay
// PROVED it made before delivery. Duplicating it would let the two drift, and
// then a reason the classifier renders as a clean stop could be a reason the
// retry refuses to act on (or, far worse, the reverse).

/**
 * One row of Clay's refusal vocabulary. The three columns are deliberately
 * split because two of them are CLAY's and one is TERRA's, and only the first
 * two may ever decide whether a real prospect is emailed again:
 *
 *   `prefix`    the machine token `Stop unsafe delivery` emits internally
 *               (`sender_not_a_salesforce_user:`).
 *   `v3Phrase`  the plain phrase V3's own `Post final status alert` node
 *               substitutes for that prefix before storing — what all 39 prod
 *               sender refusals actually carry.
 *   `repText`   the words Terra shows a rep.
 *
 * `prefix` and `v3Phrase` are the IDENTITY of V3's `Stop unsafe delivery`
 * node: they change only when Clay changes, and a Terra-side edit to either is
 * a bug, not a copy pass. `repText` is Terra's and free — reword it whenever
 * the UX wants, in this file, with no effect on eligibility.
 */
export interface BlockPhrase {
  /** Clay's machine token, with its trailing colon. Never edited Terra-side. */
  prefix: string;
  /** Clay's own plain substitution for `prefix`. Never edited Terra-side. */
  v3Phrase: string;
  /** What a rep reads. Terra's to reword; read only by the classifier. */
  repText: string;
}

/**
 * V3's `BLOCK_PREFIXES` table (`Post final status alert`, plan
 * 2026-09-17-clay-v3-orchestration-fixes): the machine prefix the
 * `Stop unsafe delivery` node emits → the plain words a rep should read. Kept
 * in the same order as V3.
 *
 * All seven originate in `Stop unsafe delivery`, which sits BEFORE
 * `Route delivery channel` in the V3 graph — which is why matching one is
 * structural proof that nothing was sent. See {@link isProvenSafeRefusal}.
 *
 * `repText === v3Phrase` for all seven today. That is a coincidence of origin
 * (V3's alert node and Terra's table were ported from one source), NOT an
 * invariant — see {@link BlockPhrase} for why the columns are separate.
 */
export const BLOCK_PREFIXES: readonly BlockPhrase[] = [
  {
    prefix: 'leftover_merge_tokens:',
    v3Phrase: 'unresolved merge fields survived rendering',
    repText: 'unresolved merge fields survived rendering',
  },
  { prefix: 'dirty_copy:', v3Phrase: 'copy QA flagged the copy', repText: 'copy QA flagged the copy' },
  { prefix: 'copy_missing:', v3Phrase: 'step 1 has no message body', repText: 'step 1 has no message body' },
  { prefix: 'no_sender:', v3Phrase: 'no sender could be resolved', repText: 'no sender could be resolved' },
  {
    prefix: 'service_account:',
    v3Phrase: 'the only sender candidate is the integration robot',
    repText: 'the only sender candidate is the integration robot',
  },
  {
    prefix: 'sender_not_a_salesforce_user:',
    v3Phrase: 'the sender is not a Salesforce user',
    repText: 'the sender is not a Salesforce user',
  },
  {
    prefix: 'sender_identity_incomplete:',
    v3Phrase: "the sender's Salesforce user has no first name",
    repText: "the sender's Salesforce user has no first name",
  },
];

/**
 * The `clay_block.node` V3 stamps on its FINAL-STATUS decision — a send Clay
 * REFUSED for a reason only a rep or an admin can act on. Declared as a literal
 * here rather than imported: this module is a DOM-free, server-free leaf a
 * mini-app frontend loads, and must not pull `src/services/**` into its graph.
 * Server-side source of truth: `FINAL_STATUS_BLOCK_NODE` in
 * `src/services/clay/routine-result-projection.ts`. A genuine routine-item
 * crash is stamped `'Clay routine run'` by `clayRoutineFailedProjection`, which
 * is the whole discriminator sharing-rules edge case (l) rests on. [R16]
 */
export const FINAL_STATUS_BLOCK_NODE = 'Compute final status';

/**
 * Which `Stop unsafe delivery` phrase does this reason come from, if any?
 * Matches either spelling the pipeline can leave in storage: the machine
 * prefix (`sender_not_a_salesforce_user: …`, what V3 emits internally) or the
 * plain phrase V3's own alert node already substituted for it (what all 39
 * prod sender refusals actually carry).
 *
 * **Reads `prefix` and `v3Phrase` only — never `repText`.** This function is
 * how Terra decides whether a real prospect may be emailed a second time, and
 * Clay puts no idempotency key on a routine-run START. Matching on the
 * rep-facing column would mean a copy-polish pass silently flipped retry
 * eligibility on every stored row of that shape. (U4)
 *
 * Matched against the RAW stored reason, ignoring only leading whitespace,
 * HTML tags and square brackets (the same noise the classifier's own cleaning
 * removes, so both agree on a reason that arrives wrapped in markup).
 * Deliberately NOT matched against the cleaned HEADLINE: this test decides
 * whether a resend is safe, and must not be able to change because someone
 * tuned the wording pass.
 *
 * `table` is a parameter so a test can prove the answer does not move when
 * `repText` does; production has exactly one table, {@link BLOCK_PREFIXES}.
 */
export function matchStopUnsafeDeliveryReasonIn(
  table: readonly BlockPhrase[],
  raw: string | null | undefined,
): BlockPhrase | null {
  const text = (raw ?? '').replace(/^(?:\s|<[^>]*>|[[\]])+/, '').toLowerCase();
  if (text === '') return null;
  return (
    table.find((phrase) => text.startsWith(phrase.prefix) || text.startsWith(phrase.v3Phrase.toLowerCase())) ?? null
  );
}

/** {@link matchStopUnsafeDeliveryReasonIn} as a predicate, for a caller-supplied table. */
export function isStopUnsafeDeliveryReasonIn(table: readonly BlockPhrase[], raw: string | null | undefined): boolean {
  return matchStopUnsafeDeliveryReasonIn(table, raw) !== null;
}

/** Which of the seven real phrases this reason came from — production's one table. */
export function matchStopUnsafeDeliveryReason(raw: string | null | undefined): BlockPhrase | null {
  return matchStopUnsafeDeliveryReasonIn(BLOCK_PREFIXES, raw);
}

/** Does this reason come from the `Stop unsafe delivery` gate? The retry arm's gate. */
export function isStopUnsafeDeliveryReason(raw: string | null | undefined): boolean {
  return isStopUnsafeDeliveryReasonIn(BLOCK_PREFIXES, raw);
}

// Retry (plan 2026-09-26-003, U6/U7; the refusal arm 2026-09-30, sharing rules (m))
//
// Two kinds of row can be retried, for two different reasons:
//
//   START FAILURE   `failed` with no Clay status at all — the send never
//                   reached Clay. Safe to resend only once Clay confirms it
//                   has no run for the member (`dispatch-retry.ts` step 4).
//   CLAY REFUSAL    `sent` + `clay_failed`, where Clay's own graph proves it
//                   refused BEFORE delivery. Nothing was sent, so nothing can
//                   double-send; the retry rebuilds the payload from the play
//                   as it is now, and must NOT ask Clay's runs query (Clay
//                   HAS a run — the refused one — and treating that as
//                   "already delivered" would mark the row sent).
//
// The server decides for real (`dispatch-retry.ts`); these helpers decide what
// the UI OFFERS and what it SAYS, and share the one eligibility rule with the
// server so the button never offers a retry the server refuses as
// `not_retryable`.

/**
 * Could a retry of the START-FAILURE kind act on this row? `failed`, not a
 * deliberate skip, and Clay never reported on it (a row with a `result_status`
 * got far enough for Clay to run it — that is not a START failure).
 */
export function isRetryableDispatchRow(
  row: Pick<PlaybooksDispatchLogRow, 'result' | 'error' | 'result_status'>,
): boolean {
  return row.result === 'failed' && !isSkippedMemberError(row.error) && row.result_status === null;
}

/**
 * Did Clay refuse this send at a gate that runs BEFORE delivery? Four
 * structural facts, all read from data the projection already stores:
 *
 *   1. `result = 'sent'`              — Terra's START landed; Clay ran it.
 *   2. `result_status = 'clay_failed'` — Clay's own terminal verdict was a failure.
 *   3. `clay_run.status = 'Error'` and
 *      `clay_block.node = 'Compute final status'`
 *                                     — V3's FINAL-STATUS decision, not a
 *                                       routine item that threw (`'Clay
 *                                       routine run'`). Edge case (l).
 *   4. the reason is one of the seven {@link BLOCK_PREFIXES} — i.e. it was
 *      produced by `Stop unsafe delivery`.
 *
 * (4) is the load-bearing one and it is NOT text-parsing the phrase "nothing
 * was sent" out of a free-text string. `Stop unsafe delivery` sits before
 * `Route delivery channel` in the V3 graph, so a reason that node produced is
 * proof of position in the graph: the run never reached a delivery channel, so
 * no email, Gong enrolment or Inflection send happened. Anything else —
 * `Gong returned no enrollment id`, `Inflection returned no success response`,
 * a run that ended with no recorded delivery, or a reason Terra does not
 * recognise — may have partly delivered, and stays non-retryable until Clay
 * can state `sent: false` itself. Sharing rules (m).
 */
export function isProvenSafeRefusal(
  row: Pick<PlaybooksDispatchLogRow, 'result' | 'result_status' | 'result_data'>,
): boolean {
  if (row.result !== 'sent' || row.result_status !== 'clay_failed') return false;
  const clay = readClayRunResultData(row.result_data);
  if (clay === null || clay.clay_run.status !== 'Error') return false;
  if (clay.clay_block?.node !== FINAL_STATUS_BLOCK_NODE) return false;
  return isStopUnsafeDeliveryReason(clay.clay_block.reason);
}

/**
 * The `clay_block.node` the projection stamps on a routine item that THREW
 * (`clayRoutineFailedProjection`, `src/services/clay/routine-result-projection.ts`)
 * — the crash side of edge case (l)'s discriminator. A literal for the same
 * reason as {@link FINAL_STATUS_BLOCK_NODE}: this module is a frontend leaf.
 */
export const ROUTINE_CRASH_BLOCK_NODE = 'Clay routine run';

/**
 * The network errors that mean Clay's own HTTP call to a tool dropped mid-run
 * (plan 2026-10-14-002 U0e, sharing rules (o)) — 16 of 1,142 runs on
 * 10/14 read `Tool execution failed with status: ERROR — read ECONNRESET`.
 * THE one table: the classifier, the retry's third arm and "Retry all"'s count
 * all read it through {@link isTransportCrash}. Matched case-insensitively as
 * a substring of the stored reason. A rep never reads these words.
 */
export const TRANSPORT_CRASH_ERRORS: readonly string[] = [
  'read ECONNRESET',
  'ETIMEDOUT',
  'ECONNREFUSED',
  'socket hang up',
  'EAI_AGAIN',
  'fetch failed',
];

/**
 * `%word%` LIKE patterns matching any of `words` as a case-insensitive
 * substring of a `lower(...)` column — lower-cased, with `\`, `%` and `_`
 * escaped (Postgres's default LIKE escape is the backslash).
 */
export function containsLikePatterns(words: readonly string[]): string[] {
  return words.map((word) => `%${word.toLowerCase().replace(/[\\%_]/g, (char) => `\\${char}`)}%`);
}

/**
 * The SQL twin of {@link TRANSPORT_CRASH_ERRORS} (second review-fix pass U7,
 * C5a-2), DERIVED from it — never a second list. Pre-narrows
 * `dispatch-recheck.ts#selectUnclaimedTransportCrashes` before its `LIMIT`;
 * {@link isTransportCrash} still decides.
 */
export function transportCrashReasonLikePatterns(): string[] {
  return containsLikePatterns(TRANSPORT_CRASH_ERRORS);
}

/**
 * Did this run break on a NETWORK error inside Clay (o)? Four structural facts
 * plus the reason:
 *
 *   1. `result = 'sent'`               — Clay ran it;
 *   2. `result_status = 'clay_failed'` — terminal failure;
 *   3. `clay_run.status = 'failed'` and `clay_block.node = 'Clay routine run'`
 *                                      — a routine item that threw, not V3's
 *                                        final-status decision (l);
 *   4. the reason carries one of {@link TRANSPORT_CRASH_ERRORS}.
 *
 * This is NOT proof nothing was sent — Terra cannot see which Clay node broke
 * (only Clay's internal API shows it). It is what makes the row RETRYABLE at
 * all: the stored job was fine, the network was not. Whether a retry may run
 * without the rep's confirmation is decided per channel by
 * `dispatch-crash-triage.ts` (test sends; Gong, after Terra checks Gong).
 */
export function isTransportCrash(
  row: Pick<PlaybooksDispatchLogRow, 'result' | 'result_status' | 'result_data'>,
): boolean {
  return row.result === 'sent' && isTransportCrashProjection(row.result_status, row.result_data);
}

/**
 * {@link isTransportCrash} over a Clay projection (`result_status` +
 * `result_data`) — for callers that hold what Clay just said rather than a
 * row (core's callback event, the re-check's settler; review-fix U13, S3).
 */
export function isTransportCrashProjection(resultStatus: string | null, resultData: unknown): boolean {
  if (resultStatus !== 'clay_failed') return false;
  const clay = readClayRunResultData(resultData);
  if (clay === null || clay.clay_run.status !== 'failed') return false;
  if (clay.clay_block?.node !== ROUTINE_CRASH_BLOCK_NODE) return false;
  const reason = clay.clay_block.reason.toLowerCase();
  if (reason === '') return false;
  return TRANSPORT_CRASH_ERRORS.some((word) => reason.includes(word.toLowerCase()));
}

/** At most this many AUTOMATIC retries of one row's network-error crashes (o). */
export const TRANSPORT_CRASH_AUTO_RETRY_MAX = 3;

const AutoRetrySliceSchema = z.object({ auto_retry: DispatchAutoRetryHistorySchema });
const CrashTriageSliceSchema = z.object({ crash_triage: DispatchCrashTriageSchema });

/** `result_data.auto_retry` (oldest first), `[]` when absent or drifted. Never throws — a display path. */
export function readAutoRetries(resultData: unknown): DispatchAutoRetryEntry[] {
  const parsed = AutoRetrySliceSchema.safeParse(resultData);
  return parsed.success ? parsed.data.auto_retry : [];
}

const PriorRunsSliceSchema = z.object({ prior_runs: DispatchPriorRunsSchema });

/** `result_data.prior_runs` (U6), `[]` when absent or drifted. Never throws. */
export function readPriorRuns(resultData: unknown): DispatchPriorRun[] {
  const parsed = PriorRunsSliceSchema.safeParse(resultData);
  return parsed.success ? parsed.data.prior_runs : [];
}

/** `result_data.crash_triage`, or `null`. Never throws. */
export function readCrashTriage(resultData: unknown): DispatchCrashTriage | null {
  const parsed = CrashTriageSliceSchema.safeParse(resultData);
  return parsed.success ? parsed.data.crash_triage : null;
}

/** How long a retried row may sit `queued` with nobody picking it up before it counts as stranded (U6). */
export const STRANDED_RETRY_AFTER_MS = 10 * 60_000;

const AttemptOwnerSliceSchema = z.object({ attempt_owner: z.string().min(1) });

/**
 * A retry whose child was never started (review-fix U6, C3-2/C5-2): a rep's
 * Retry claims the row back to `queued` and THEN enqueues its child, so a
 * process death in between leaves a `queued` row no retry arm matches. Every
 * clause, in order: still `queued`; retried at least once; never picked up by
 * a child (`processing_at` unset, no `attempt_owner` claim — U5); retried at
 * least {@link STRANDED_RETRY_AFTER_MS} ago; and not Terra's own automatic
 * retry (`crash_triage.outcome = 'requeued'`), which the durable triage starts
 * and cannot strand. A row merely waiting its turn in the paced queue also
 * matches — recovery is a deduplicated start, a no-op for a run that exists.
 * The SQL twin (plus `payload IS NOT NULL`, a column the summary does not
 * load) is `dispatch-settle-sweep.ts#STRANDED_RETRY_SQL`.
 */
export function isStrandedRetryRow(
  row: Pick<PlaybooksDispatchLogRow, 'result' | 'processing_at' | 'retry_count' | 'last_retry_at' | 'result_data'>,
  nowMs: number,
): boolean {
  if (row.result !== 'queued' || (row.retry_count ?? 0) === 0 || row.processing_at !== null) return false;
  if (AttemptOwnerSliceSchema.safeParse(row.result_data).success) return false;
  const retriedAt = row.last_retry_at === null || row.last_retry_at === undefined ? Number.NaN : Date.parse(row.last_retry_at);
  if (!Number.isFinite(retriedAt) || nowMs - retriedAt < STRANDED_RETRY_AFTER_MS) return false;
  return readCrashTriage(row.result_data)?.outcome !== 'requeued';
}

/**
 * Was a 429 from Clay's START what stopped this row (U0e item 1)? A refused
 * admission — Clay created no run — so the retry's runs check finds none and
 * resends. Read from the raw `error` launcher.dispatch writes (`… HTTP 429: …`),
 * never shown to a rep.
 */
export function isClayBusyStartFailure(row: Pick<PlaybooksDispatchLogRow, 'result' | 'error' | 'result_status'>): boolean {
  return isRetryableDispatchRow(row) && /\bHTTP 429\b/.test(row.error ?? '');
}

/**
 * The four retry arms (sharing rules (m), (o), (u)) — the ONE decision every
 * reader shares: the row's Retry button, "Retry all"'s count, the send summary
 * and the server's eligibility test, so none of them can offer a retry another
 * refuses.
 *
 *   `start`          a START that never reached Clay (the runs-query arm);
 *   `refusal`        a proven-safe `Stop unsafe delivery` refusal (rebuild);
 *   `network_crash`  a run Clay broke on a network error (same stored job);
 *   `clay_failure`   EVERY OTHER Clay-failed row (same stored job; PR 1 U2).
 */
export type RetryArm = 'start' | 'refusal' | 'network_crash' | 'clay_failure';

const ClayRunObjectSchema = z.object({ clay_run: z.record(z.string(), z.unknown()) });

/**
 * Clay's status word and routine run id from `result_data.clay_run` ALONE —
 * independent of every optional sibling (`clay_preview`, `clay_checks`, …) AND
 * of each other: each field is read on its own, so a missing or null `status`
 * never discards a valid `routine_run_id` (the claim's fence), and a missing
 * `routine_run_id` never hides the status word. `readClayRunResultData` parses
 * the whole object, so one malformed field makes it return `null`; the retry's
 * eligibility exclusions and its claim fence must not depend on that. `null`
 * only when `clay_run` itself is missing or not an object.
 */
export function readClayRunIdentity(
  resultData: unknown,
): { status: string | null; routineRunId: string | null } | null {
  const parsed = ClayRunObjectSchema.safeParse(resultData);
  if (!parsed.success) return null;
  const { status, routine_run_id: routineRunId } = parsed.data.clay_run;
  return {
    status: typeof status === 'string' ? status : null,
    routineRunId: typeof routineRunId === 'string' ? routineRunId : null,
  };
}

/**
 * Is this a Clay-failed row on the general arm? `sent` + `clay_failed`, minus a
 * deliberate non-send word that older rows stored under `clay_failed`
 * (`Invalid Email`, `Suppressed`, … — Clay deciding not to send, which
 * resending the same job cannot change) and a test preview. Unreadable
 * `result_data` counts: the rep is shown "Clay didn't say why" and still decides.
 *
 * NOT a claim that nothing was delivered — that is the point of the arm. See
 * `dispatch-retry.ts`'s header and sharing rules (u).
 */
function isClayFailure(row: Pick<PlaybooksDispatchLogRow, 'result' | 'result_status' | 'result_data'>): boolean {
  if (row.result !== 'sent' || row.result_status !== 'clay_failed') return false;
  // The status word is read from `clay_run` ALONE: a malformed OPTIONAL field
  // elsewhere (`clay_preview: null`) must never hide a deliberate non-send.
  const word = readClayRunIdentity(row.result_data)?.status;
  // No readable status word is simply "not an excluded word".
  if (word === undefined || word === null) return true;
  return !(CLAY_FINAL_STATUS_NON_SEND as readonly string[]).includes(word) && word !== 'Test preview' && word !== 'test_preview';
}

/**
 * Which arm would a retry of this row take, or `null` when none. Precedence is
 * the server's: START, refusal, network crash, then the general arm — the
 * first three are strictly narrower proofs, so a row never reads as two.
 */
export function retryArmOf(
  row: Pick<PlaybooksDispatchLogRow, 'result' | 'error' | 'result_status' | 'result_data'>,
): RetryArm | null {
  if (isRetryableDispatchRow(row)) return 'start';
  if (isProvenSafeRefusal(row)) return 'refusal';
  if (isTransportCrash(row)) return 'network_crash';
  if (isClayFailure(row)) return 'clay_failure';
  return null;
}

/** Any retryable arm — what a Retry button may be offered on at all. */
export function isRetryEligibleRow(
  row: Pick<PlaybooksDispatchLogRow, 'result' | 'error' | 'result_status' | 'result_data'>,
): boolean {
  return retryArmOf(row) !== null;
}

/**
 * Must the rep confirm before this row is retried? Both arms that resend the
 * SAME stored job for a run Terra cannot prove delivered nothing — a network
 * crash and a general Clay failure — on a REAL send. A test send never
 * delivers, and the START / refusal arms have their own proofs.
 */
export function retryNeedsConfirm(
  row: Pick<PlaybooksDispatchLogRow, 'result' | 'error' | 'result_status' | 'result_data' | 'is_test'>,
): boolean {
  const arm = retryArmOf(row);
  return (arm === 'network_crash' || arm === 'clay_failure') && row.is_test !== true;
}

/**
 * How many rows of one send "Retry all stopped or failed" would act on — every
 * arm, which is exactly why the button does not say "failed" on its own (m).
 */
export function retryEligibleCount(
  rows: ReadonlyArray<Pick<PlaybooksDispatchLogRow, 'result' | 'error' | 'result_status' | 'result_data'>>,
): number {
  return rows.filter(isRetryEligibleRow).length;
}

/** Shown while `launcher.dispatch` is still asking Clay about an ambiguous START. */
export const DISPATCH_RECONCILING_CAPTION = 'Checking with Clay whether this run started…';

/**
 * The plain-English headline for a START failure, from
 * `result_data.dispatch_failure` — or {@link DISPATCH_RECONCILING_CAPTION}
 * while the workflow is still checking. `null` when there is nothing more to
 * say than the existing `dispatchStartFailureSummary` (a definite refusal
 * such as a 400, which that helper already explains), and for every other
 * row. Never a status code; the raw `error` stays behind "Details".
 */
export function dispatchFailureSummary(
  row: Pick<PlaybooksDispatchLogRow, 'result' | 'result_data'>,
): string | null {
  if (row.result === 'processing' && readDispatchReconcile(row.result_data) !== null) {
    return DISPATCH_RECONCILING_CAPTION;
  }
  if (row.result !== 'failed') return null;
  const failure = readDispatchFailure(row.result_data);
  if (failure === null || !failure.ambiguous) return null;

  const what =
    failure.reason === 'no_response'
      ? "Clay didn't answer when we tried to start this send"
      : 'Clay had a problem starting this send';
  if (failure.reconciled === 'none') {
    return `${what}. Clay has no run for this person, so it's safe to retry.`;
  }
  if (failure.reconciled === 'no_sibling_runs') {
    return `${what}, and Clay shows no runs at all for this send, so we couldn't confirm whether it started. Check the send in Clay before retrying.`;
  }
  return `${what}, and we couldn't confirm with Clay whether it started. Retry checks with Clay first.`;
}

/** `Jane Doe` for one named row, `3 people` for several (or one with no name). */
function whoLabel(results: PlaybooksRetryDispatchResponse['results']): string {
  if (results.length === 1 && results[0].contactName) return results[0].contactName;
  return `${results.length} ${results.length === 1 ? 'person' : 'people'}`;
}

/**
 * The toast after a retry — one sentence per kind of outcome, requeued first,
 * in plain English (no ids, no status codes). `status` is `success` only when
 * every row either went back out or turned out to be already running in Clay;
 * anything that needs the rep's attention is `info`. A failed REQUEST (the
 * endpoint refused or was unreachable) is not this toast — the caller shows
 * the server's own `userMessage` via `addErrorToast`.
 */
export function retryResultToast(response: PlaybooksRetryDispatchResponse): {
  content: string;
  status: 'success' | 'info';
} {
  const by = (outcome: PlaybooksRetryDispatchOutcome) =>
    response.results.filter((r) => r.outcome === outcome);
  const acted = response.results.filter((r) => r.outcome !== 'not_retryable');
  if (acted.length === 0) return { content: 'Nothing to retry in this send.', status: 'info' };

  const parts: string[] = [];
  if (response.requeued > 0) parts.push(`Requeued ${response.requeued} of ${acted.length}.`);
  const hasRun = by('clay_has_run');
  if (hasRun.length > 0) parts.push(`Clay already has a run for ${whoLabel(hasRun)} — marked as sent.`);
  const unavailable = by('clay_unavailable');
  if (unavailable.length > 0) {
    const who = unavailable.length === 1 && acted.length === 1 ? '' : ` ${whoLabel(unavailable)}`;
    parts.push(`Couldn't reach Clay to check${who} — nothing was resent. Try again in a minute.`);
  }
  const tooRecent = by('too_recent');
  if (tooRecent.length > 0) {
    const who = tooRecent.length === 1 && acted.length === 1 ? '' : ` ${whoLabel(tooRecent)}`;
    parts.push(`Give Clay a minute, then retry${who}.`);
  }
  const noRuns = by('clay_no_runs');
  if (noRuns.length > 0) {
    parts.push(
      `Clay shows no runs at all for this send, so we can't confirm whether ${whoLabel(noRuns)} started — nothing was resent. Check the send in Clay.`,
    );
  }
  const already = by('already_retried');
  if (already.length > 0) {
    parts.push(`${whoLabel(already)} ${already.length === 1 ? 'is' : 'are'} already being retried.`);
  }
  const noPayload = by('no_payload');
  if (noPayload.length > 0) {
    parts.push(`We no longer have what was sent to ${whoLabel(noPayload)}, so nothing was resent.`);
  }
  // Sharing rules (m): a Clay refusal whose message could not be rebuilt from
  // the play as it is now. Names both things a rep can check, because those
  // are the only two reasons the rebuild refuses.
  const rebuildFailed = by('copy_rebuild_failed');
  if (rebuildFailed.length > 0) {
    parts.push(
      `We couldn't rebuild the message for ${whoLabel(rebuildFailed)} from the play as it is now, so nothing was resent. Check that they're still in the play, and that the copy has no unfilled merge fields.`,
    );
  }
  const startFailed = by('start_failed');
  if (startFailed.length > 0) parts.push(`Couldn't start the resend for ${whoLabel(startFailed)}. Try again.`);

  const allGood = acted.every((r) => r.outcome === 'requeued' || r.outcome === 'clay_has_run');
  return { content: parts.join(' '), status: allGood ? 'success' : 'info' };
}
